import React from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { addHours, isBefore } from 'date-fns'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import RefreshButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/refreshButton/RefreshButton'
import Dashlet from '@lyrahealth-inc/ui-core/lib/molecules/layouts/dashlet/Dashlet'
import { IconBadge } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/iconBadge/IconBadge'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { BadgeShape, BadgeVariant } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './unclosedSessionsDashlet.module.scss'
import { appointmentStatuses } from '../../../common/constants/appConstants'
import { CLIENT_ACTIVITIES } from '../../../common/constants/routingConstants'
import { getClientFullName } from '../../../common/utils/utils'
import { useAppDispatch } from '../../../data/store'
import { useGetAppointmentsQuery } from '../../clients/clientDetails/data/appointmentsApi'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { isProviderAppointment } from '../dashboardUtils'
import { useDashboardClientsData } from '../hooks/useDashboardClientsData'

export const UnclosedSessionsDashlet: React.FC<UnclosedSessionsDashletProps> = ({ providerId }) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isPreferredNameEnabled } = useFlags()
  const { activeAndInteractedClients: clients, isLoading } = useDashboardClientsData()

  const {
    data: appointments = [],
    refetch: refetchAppointments,
    isFetching: isRefreshingSessions,
  } = useGetAppointmentsQuery({ providerId })
  const unclosedAppointments = appointments.filter((appt: Appointment) => {
    const clientForAppointment = Array.isArray(clients) && clients?.find((client) => client.id === appt?.userId)
    return (
      isProviderAppointment(appt, providerId) &&
      isBefore(getDateFromAppointment(appt), new Date()) &&
      ![appointmentStatuses.completed, appointmentStatuses.missed, appointmentStatuses.canceled].includes(
        appt.appointmentStatus,
      ) &&
      clientForAppointment
    )
  })

  const renderSessionListItem = (appt: Appointment) => {
    const appointmentTime = getDateFromAppointment(appt)
    const clientForAppointment = Array.isArray(clients) && clients?.find((client: any) => client.id === appt?.userId)
    if (!clientForAppointment) {
      // In the weird case that a client cannot be found, don't show a list item
      return
    }
    return (
      <div className={styles['dashlet-item-container']} key={`${clientForAppointment.first_name}${appt.startDate}`}>
        <button
          type='button'
          className={styles['dashlet-item-content']}
          onClick={() => {
            dispatch(setSelectedClientId(clientForAppointment.id))
            navigate(CLIENT_ACTIVITIES.route)
          }}
          data-test-id={`UnclosedSessions-list-item-${clientForAppointment.first_name}-${appt.startDate}`}
        >
          <div className={styles.left}>
            <div className={styles.appointmentTime}>
              <div className={styles.date}>
                {intl.formatDate(appointmentTime, { year: 'numeric', month: '2-digit', day: '2-digit' })}
              </div>
              <div className={styles.subtext}>
                {intl.formatDate(appointmentTime, { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <div className={styles.appointmentInfo}>
              <div>{getClientFullName(clientForAppointment, isPreferredNameEnabled)}</div>
              <div className={styles.subtext}>{`Session ${appt.sessionNumber}`}</div>
            </div>
          </div>
          <div className={styles.right}>
            {isBefore(addHours(appointmentTime, 24), new Date()) && (
              <IconBadge
                text='overdue'
                variant={BadgeVariant.ALERT}
                shape={BadgeShape.RECT}
                testID={tID('IconBadge-overdue')}
              />
            )}
            <ChevronIcon direction={ChevronIconDirection.RIGHT} size={13} fillColor={styles.x_soft_black} />
          </div>
        </button>
      </div>
    )
  }

  return (
    <div className={styles['dashlet-container']} data-test-id='UnclosedSessionDashlet-content'>
      <Dashlet
        title={`Unclosed sessions${isLoading ? '' : ` (${unclosedAppointments.length})`}`}
        buttons={<RefreshButton size={'20'} isLoading={isRefreshingSessions} onClick={refetchAppointments} />}
        isLoading={isLoading}
      >
        {unclosedAppointments.length === 0 ? (
          <div className={styles['empty-dashlet']}>No sessions to close</div>
        ) : (
          unclosedAppointments.map(renderSessionListItem)
        )}
      </Dashlet>
    </div>
  )
}

type UnclosedSessionsDashletProps = {
  providerId: string
}

export default UnclosedSessionsDashlet
